import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { ThreadEntry } from "../../../api/threads/liveMessages";
import ErrorNotice from "../../../errors/ErrorNotice";
import AppButton from "../../../ui/buttons/AppButton";
import LoadingIndicator from "../../../ui/feedback/LoadingIndicator";
import SpinnerIcon from "../../../ui/feedback/SpinnerIcon";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import { markRead } from "../data/readMarks";
import { useThreadLiveClient } from "../live/threadLiveContext";
import { lastNumberOf } from "../live/threadLog";
import type { LiveThreadSnapshot } from "../live/threadLiveClient";
import type { TurnProgressView } from "../live/turnProgress";
import { entryAnchor } from "./entryAnchors";
import { logView, type AgentTurn, type TurnStep } from "./logBlocks";
import ThreadEntryView from "./ThreadEntryView";
import ToolCallGroupView from "./ToolCallGroupView";
import ToolStepBlockView from "./ToolStepBlockView";

interface EntryLogProps {
  threadId: string;
  live: LiveThreadSnapshot | undefined;
  /** The entry the address names, scrolled to once it is held. */
  highlightedEntry: number | null;
  /**
   * Whether new entries keep the view at the log's end; not while the current step below asks the
   * viewer something, which then keeps the view (usability round 2, 4 Nov).
   */
  followEnd: boolean;
}

/**
 * The Thread's log, strictly in entry order (threads.md T-1): earlier entries on request,
 * then everything held, then what the running turn is doing. While the user is at the end,
 * new entries keep the view at the end; the entries shown count as read.
 */
export default function EntryLog({ threadId, live, highlightedEntry, followEnd }: EntryLogProps) {
  const { t } = useTranslation();
  const client = useThreadLiveClient();
  const endRef = useRef<HTMLDivElement>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const followingEnd = useRef(true);
  const scrolledToHighlight = useRef<number | null>(null);
  const lastNumber = live === undefined ? 0 : lastNumberOf(live.log);
  const entries = live?.log.entries;
  const view = useMemo(() => logView(entries ?? []), [entries]);
  // The open turn's own progress: the agent's text, plan and calls; its tool runs show theirs in their blocks (R3-2).
  const openTurnId = live?.state?.facts.detail.open_turn?.turn_id;
  const openProgress = openTurnId == null ? undefined : live?.progress.get(openTurnId);

  useEffect(() => {
    const end = endRef.current;
    if (end === null) {
      return undefined;
    }
    const observer = new IntersectionObserver(([observed]) => {
      followingEnd.current = observed?.isIntersecting ?? false;
    });
    observer.observe(end);
    return () => {
      observer.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    if (highlightedEntry !== null && scrolledToHighlight.current !== highlightedEntry) {
      // In this log only: another tab may show another Thread with the same entry number (F90).
      const element = logRef.current?.querySelector(`[id="${entryAnchor(highlightedEntry)}"]`) ?? null;
      if (element !== null) {
        scrolledToHighlight.current = highlightedEntry;
        element.scrollIntoView({ block: "center" });
        return;
      }
    }
    if (followingEnd.current && followEnd && highlightedEntry === null) {
      endRef.current?.scrollIntoView({ block: "end" });
    }
  }, [lastNumber, highlightedEntry, openProgress, followEnd]);

  useEffect(() => {
    if (lastNumber > 0 && document.visibilityState === "visible") {
      markRead(threadId, lastNumber);
    }
  }, [threadId, lastNumber]);

  if (live === undefined || (live.log.entries.length === 0 && live.delivery === "subscribing")) {
    return <LoadingIndicator label={t("threads.log.loading")} className="py-10" />;
  }

  return (
    <div ref={logRef} className="space-y-2">
      {live.log.base > 0 ? (
        <div className="flex justify-center">
          <AppButton variant="ghost" size="xs" disabled={live.loadingEarlier} onClick={() => { void client.loadEarlier(threadId); }}>
            {live.loadingEarlier ? t("common.loading") : t("threads.log.earlier", { count: live.log.base })}
          </AppButton>
        </div>
      ) : null}
      {live.log.violations.length > 0 ? (
        <StatusMessage tone="warning" title={t("threads.log.orderBrokenTitle")}>
          {t("threads.log.orderBrokenBody", { count: live.log.violations.length })}
        </StatusMessage>
      ) : null}
      {live.error === null ? null : <ErrorNotice error={live.error} />}
      <ol className="space-y-2" aria-label={t("threads.log.label")}>
        {view.blocks.map((block) =>
          block.kind === "toolStep" ? (
            <ToolStepBlockView key={block.request.number} block={block} />
          ) : block.kind === "toolCalls" ? (
            <ToolCallGroupView key={block.calls[0]?.number} block={block} threadId={threadId} highlightedEntry={highlightedEntry} />
          ) : (
            <ThreadEntryView
              key={block.entry.number}
              entry={block.entry}
              turnStep={turnStepOf(block.entry, view.turnSteps)}
              agentTurn={agentTurnOf(block.entry, view.agentTurns)}
              reviewed={block.entry.payload.kind === "human_input" ? view.reviewed.get(block.entry.payload.answers_entry) : undefined}
              request={block.entry.payload.kind === "human_input" ? entries?.find((entry) => block.entry.payload.kind === "human_input" && entry.number === block.entry.payload.answers_entry)?.payload : undefined}
            />
          ),
        )}
      </ol>
      {openProgress === undefined ? null : <LiveTurnProgress progress={openProgress} />}
      {live.delivery === "refilling" ? <LoadingIndicator label={t("threads.log.refilling")} /> : null}
      {/* The composer below sticks over the end; a large scroll margin scrolls past it. */}
      <div ref={endRef} aria-hidden="true" className="h-px scroll-mb-[100vh]" />
    </div>
  );
}

/** The agent turn that wrote an answer or output, for who answered (F70). */
function agentTurnOf(entry: ThreadEntry, agentTurns: ReadonlyMap<string, AgentTurn>): AgentTurn | null {
  const { payload } = entry;
  return payload.kind === "step_output" || payload.kind === "turn_finished" ? (agentTurns.get(payload.turn_id) ?? null) : null;
}

/** The step whose turn wrote an output, for its title. */
function turnStepOf(entry: ThreadEntry, turnSteps: ReadonlyMap<string, TurnStep>): TurnStep | null {
  const { payload } = entry;
  return payload.kind === "step_output" || payload.kind === "turn_finished" ? (turnSteps.get(payload.turn_id) ?? null) : null;
}

/** What the running turn has written and called so far; replaced by its result. */
function LiveTurnProgress({ progress }: { progress: TurnProgressView }) {
  const { t } = useTranslation();
  return (
    <div className="app-card space-y-2 border-l-4 border-l-[var(--app-accent)] p-3" aria-live="polite">
      <p className="app-title flex items-center gap-2 text-sm font-semibold">
        <SpinnerIcon className="h-4 w-4" />
        {t("threads.progress.working")}
      </p>
      {progress.messages
        .filter((text) => text !== "")
        .map((text, index) => (
          <p key={String(index)} className="app-text whitespace-pre-wrap text-sm">{text}</p>
        ))}
      {progress.plan.length === 0 ? null : (
        <ul className="app-text-muted space-y-0.5 text-xs">
          {progress.plan.map((step, index) => (
            <li key={`${String(index)}-${step.content}`}>{t(`threads.progress.planStatus.${step.status}`)} · {step.content}</li>
          ))}
        </ul>
      )}
      {progress.toolCalls.length === 0 ? null : (
        <details>
          <summary className="app-text-muted cursor-pointer text-xs">{t("threads.progress.toolCalls", { count: progress.toolCalls.length })}</summary>
          <ul className="app-text-muted mt-1 space-y-0.5 text-xs">
            {progress.toolCalls.map((call) => (
              <li key={call.tool_call_id}>{call.title} · {t(`threads.progress.toolStatus.${call.status}`)}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
